export type PhotoRef = {
  kind: 'raw' | 'packed' | 'zip'
  offset: number
  size: number
  uncompressedSize: number
  method: number
  name: string
  encrypted?: true
}

export type ImageSlot = {
  photo: PhotoRef
  ambiguous: boolean
}

const IMAGE_RE = /\.(jpe?g|jpe|jfif|png|webp)$/i

export const normalizePath = (path: string): string =>
  path.replace(/\\/g, '/').replace(/^\.\//, '').replace(/\/+/g, '/').toLowerCase()

export const suffixKeys = (path: string): string[] => {
  const parts = normalizePath(path).split('/').filter(Boolean)
  const keys: string[] = []
  for (let n = Math.min(4, parts.length); n >= 1; n--) keys.push(parts.slice(parts.length - n).join('/'))
  return keys
}

export const isImageName = (name: string): boolean => IMAGE_RE.test(name)

export const isDocumentationGeojson = (name: string): boolean => {
  const parts = name.replace(/\\/g, '/').replace(/^\.\//, '').split('/').filter(Boolean)
  if (parts.length === 0 || parts.length > 2) return false
  return parts[parts.length - 1].toLowerCase() === 'documentation.geojson'
}

export const isIgnoredEntry = (name: string): boolean => {
  if (name.includes('__MACOSX/')) return true
  const base = name.split('/').pop() ?? name
  return base.startsWith('.')
}

const samePhoto = (a: PhotoRef, b: PhotoRef): boolean => a.offset === b.offset && a.size === b.size && a.name === b.name

export const buildImageIndex = (photos: PhotoRef[]): Map<string, ImageSlot> => {
  const byBase = new Map<string, PhotoRef[]>()
  for (const photo of photos) {
    const base = suffixKeys(photo.name).at(-1)
    if (!base) continue
    const list = byBase.get(base) ?? []
    list.push(photo)
    byBase.set(base, list)
  }

  const index = new Map<string, ImageSlot>()
  for (const [base, list] of byBase) {
    if (list.length === 1) {
      index.set(base, { photo: list[0], ambiguous: false })
      continue
    }
    index.set(base, { photo: list[0], ambiguous: true })
    for (const photo of list) {
      for (const key of suffixKeys(photo.name)) {
        if (key === base) continue
        const known = index.get(key)
        if (!known) index.set(key, { photo, ambiguous: false })
        else if (!samePhoto(known.photo, photo)) known.ambiguous = true
      }
    }
  }
  return index
}

export const resolveImage = (index: Map<string, ImageSlot>, path: string): PhotoRef | null => {
  if (!path) return null
  const keys = suffixKeys(path)
  for (const key of keys) {
    const hit = index.get(key)
    if (hit && !hit.ambiguous) return hit.photo
  }
  for (const key of keys) {
    const hit = index.get(key)
    if (hit) return hit.photo
  }
  return null
}
